package fyi.moko.hotpl.data.network

import dev.gitlive.firebase.firestore.firestore
import dev.gitlive.firebase.Firebase
import fyi.moko.hotpl.domain.model.YellowPageEntry

/**
 * GitLive Firestore implementation of [NetworkYellowPageDataSource].
 * Reads the `/yellow_pages` collection filtered by [regionKey].
 */
class FirebaseYellowPageDataSource(
    emulatorHost: String? = null,
) : NetworkYellowPageDataSource {
    private val firestore = Firebase.firestore

    init {
        if (emulatorHost != null) {
            try {
                firestore.useEmulator(emulatorHost, 8080)
            } catch (e: Exception) {
                // Ignore re-configuration errors, mirroring the place data source.
            }
        }
    }

    override suspend fun fetchEntriesByRegion(regionKey: String): List<YellowPageEntry> {
        // The status filter must be part of the query: the production rules only allow
        // reading APPROVED docs, and Firestore rules are constraints, not filters — an
        // unfiltered region query would be rejected outright. The client-side filter
        // below stays as defense-in-depth.
        val snapshot = firestore
            .collection("yellow_pages")
            .where { "regionKey" equalTo regionKey }
            .where { "status" equalTo YellowPageEntry.STATUS_APPROVED }
            .get()
        return snapshot.documents.mapNotNull { doc ->
            try {
                doc.data<YellowPageEntryDto>().toDomain()
            } catch (e: Exception) {
                // Tolerate a single bad doc rather than killing the whole region fetch.
                println("FirebaseYellowPageDataSource: ${doc.id} failed to decode: ${e.message ?: e::class.simpleName}")
                null
            }
        }.filter { it.status == YellowPageEntry.STATUS_APPROVED }
    }

    override suspend fun submitPendingEntry(entry: YellowPageEntry) {
        val docRef = firestore
            .collection("yellow_pages")
            .document(entry.entryId)
        // entryId is deterministic (geohash + normalized name), so a re-import of the same
        // physical place collides with any existing doc. Never demote an APPROVED entry back
        // to PENDING. The rules only permit reading APPROVED docs, so a successful get means
        // "approved — leave it alone"; a denied/missing get means the doc is absent or still
        // PENDING, and the set below is then a create or a harmless PENDING→PENDING refresh
        // (anything else is rejected server-side by the rules).
        val approvedAlready = try {
            docRef.get().exists
        } catch (e: Exception) {
            false
        }
        if (approvedAlready) return
        // Encode via the @Serializable DTO — the domain model has no kotlinx serializer
        // and would throw "Serializer for class 'YellowPageEntry' is not found" here.
        docRef.set(entry.copy(status = YellowPageEntry.STATUS_PENDING).toDto())
    }
}
